import { inject } from '@angular/core';
import { Routes, Router } from '@angular/router';
import { provideAdminPlatform } from './platform/admin-platform';
import { AdminShellService } from './platform/admin-shell.service';
import { AdminNotificationsService } from './notifications/admin-notifications.service';
import { spaceDetailCanDeactivateGuard } from '../core/guards/space-detail-can-deactivate.guard';
import { provideCampusMapEngine } from '../features/estate/campuses/map-engine.providers';
import { authenticatedAdminGuard } from '../core/guards/authenticated-admin.guard';
import { featureFlagGuard } from '../core/guards/feature-flag.guard';
import { LEGACY_POLICY_REDIRECTS } from './policy-legacy-redirects';
import { policyManagementGuard } from '../core/guards/policy-management.guard';
import { roleDetailCanDeactivateGuard } from '../core/guards/role-detail-can-deactivate.guard';
import { devOnlyGuard } from '../core/guards/dev-only.guard';
import { buildingAlertManagementGuard } from '../core/guards/building-alert-management.guard';
import { gamificationAnalyticsGuard } from '../core/guards/gamification-analytics.guard';
import { anyActionGuard } from '../core/guards/action.guard';
import { platformOperatorGuard } from '../core/guards/platform-operator.guard';
import { Action } from '../core/constants/action-catalogue';
import {
  GAMES_SETTINGS_API,
  LiveGamesSettingsApi,
} from '../features/settings/pages/games/games-settings.service';
import { questionnaireTabRedirect } from '../features/visitors/visitor-questionnaire-redirect';
import { visitorEmailLinkGuard } from './email-templates/visitor-email-links';

/**
 * The migrated admin pages, mounted at the settled GROUPED rail URLs
 * (`/estate/*`, `/workplace/*`, `/servicing/*`, `/people/*`, `/governance/*`,
 * `/communications/*`, `/platform/*`) and hosted INSIDE the ws-root Paper shell
 * (top bar + rail). This is a COMPONENTLESS layout route: it renders no chrome
 * of its own — children drop straight into ws-root's `<router-outlet>` — but it
 * still provides the admin platform contract + shell/notifications services the
 * pages inject. (The old self-contained `/admin` `AdminShellComponent` mount is
 * retired; ws-root is the single shell.)
 *
 * Spaces (list + detail + sub-types) REUSES the existing, fully-wired
 * `features/settings/pages/spaces/*` components — mounted inside this shell so
 * /admin is the single Spaces admin surface (full workbench parity, zero
 * functionality loss); `/settings/spaces*` redirects here. Policies (the
 * Policy V2 workspace) are likewise rooted in here (below) and
 * older policy URLs redirect to the list (policy-legacy-redirects.ts).
 *
 * Route-only migration: EVERY remaining /settings admin page now mounts its
 * EXISTING component (no re-skin) under this shell, and the corresponding
 * `/settings/*` routes are query/param-preserving redirects here. Children
 * marked literal-before-param (e.g. `assets/new` before `assets/:id`, the
 * three `integrations/*` literals before `integrations/:provider`,
 * `onboarding/greenfield` after `onboarding`) keep deep-links resolving.
 * Pages gated on catalogue feature flags (alerts / content / catering)
 * carry `featureFlagGuard(...)`; everything else inherits the parent
 * [authGuard, adminGuard].
 */
export const ADMIN_ROUTES: Routes = [
  {
    path: '',
    // Cookie auth (bd 2dv): authGuard validates the session cookie (viewer
    // probe → login-web redirect on failure); adminGuard then gates on an
    // admin-class action. Both no-op under `?adminPreview=1` (dev bypass) and
    // `?testHarness=1`. Children inherit this pair.
    canActivate: [authenticatedAdminGuard],
    providers: [provideAdminPlatform(), AdminShellService, AdminNotificationsService],
    children: [
      {
        path: 'platform/access-status',
        loadComponent: () => import('./admin-access-page.component').then(m => m.AdminAccessPageComponent),
        title: 'Access — Admin',
      },
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        loadComponent: () =>
          import('./pages/admin-overview-page.component').then((m) => m.AdminOverviewPageComponent),
        title: 'Overview — Admin',
      },
      {
        // REPLAYS THE INTRO over the Overview. The intro service opens it on
        // any navigation that settles on `/welcome`, so this path SERVES a
        // page rather than redirecting to one (the employee app's `/welcome`
        // works the same way). Closing the intro moves on to `/overview`, so a
        // reload does not open it again. Inherits the admin layout's guards.
        path: 'welcome',
        loadComponent: () =>
          import('./pages/admin-overview-page.component').then((m) => m.AdminOverviewPageComponent),
        title: 'Welcome — Admin',
      },
      {
        // Ask, the full page: the Settings conversation with the admin's saved
        // conversations beside it (the Overview and the "Ask Admin" panel draw
        // the same thread).
        path: 'ask',
        loadComponent: () =>
          import('../features/settings-ask/page/settings-ask-page.component').then(
            (m) => m.SettingsAskPageComponent,
          ),
        title: 'Ask — Admin',
      },
      {
        // Help for admins (Help centre v2): the admin articles of the
        // employee app's Help, behind the same `help.centre` capability.
        // `help/:articleId` reads one article; `?topic=` lists one topic.
        path: 'help',
        canActivate: [featureFlagGuard('help.centre')],
        loadComponent: () => import('./help/admin-help-page.component').then((m) => m.AdminHelpPageComponent),
        title: 'Help — Admin',
      },
      {
        path: 'help/:articleId',
        canActivate: [featureFlagGuard('help.centre')],
        loadComponent: () => import('./help/admin-help-page.component').then((m) => m.AdminHelpPageComponent),
        title: 'Help — Admin',
      },
      {
        path: 'communications/notifications',
        loadComponent: () =>
          import('./notifications/admin-notifications-page.component').then(
            (m) => m.AdminNotificationsPageComponent,
          ),
        title: 'Notifications — Admin',
      },
      {
        // Sibling to the inbox — lists every notification type the platform
        // can send and lets the admin send a test to themselves (default) or
        // a chosen colleague. Rail active-key = segs[1] == 'notification-testing'.
        path: 'communications/notification-testing',
        loadComponent: () =>
          import('./notification-testing/admin-notification-testing-page.component').then(
            (m) => m.AdminNotificationTestingPageComponent,
          ),
        title: 'Notification testing — Admin',
      },
      {
        // Branded transactional + notification email templates: catalogue +
        // sandboxed live preview (subject / HTML / plain text) per locale +
        // optional division, with a "Send test" reusing sendTestNotification.
        // Rail active-key = segs[1] == 'email-templates'.
        // Every email except `visitor:*`, which moved to Visitors › Visitor
        // emails; a link from before the move is sent on to where it lives.
        path: 'communications/email-templates',
        canActivate: [visitorEmailLinkGuard],
        loadComponent: () =>
          import('./email-templates/admin-email-templates-page.component').then(
            (m) => m.AdminEmailTemplatesPageComponent,
          ),
        data: { emailScope: 'core' },
        title: 'Email templates — Admin',
      },
      {
        // Per-organisation reusable Media Library — uploaded assets (logos,
        // banners, login media, favicons, screen backgrounds, images) with
        // size variants. The org branding page + media picker read off it.
        // Rail active-key = segs[1] == 'media'.
        path: 'platform/media',
        loadComponent: () =>
          import('./media/admin-media-page.component').then((m) => m.AdminMediaPageComponent),
        title: 'Media library — Admin',
      },
      // Spaces — the NEW-design list (paper-admin-table) wired to real data; the
      // detail (below) reuses the existing rich sections re-skinned into the
      // new shell chrome.
      {
        path: 'workplace/spaces',
        loadComponent: () =>
          import('./spaces/admin-spaces-page.component').then((m) => m.AdminSpacesPageComponent),
        title: 'Spaces — Admin',
      },
      {
        // Top-level Workplace rail item (id `sub-types`) — lives at
        // /admin/sub-types (NOT under spaces/) so the rail highlights it
        // (active key = segs[1]). /settings/spaces/sub-types redirects here.
        path: 'workplace/sub-types',
        loadComponent: () =>
          import('../features/settings/pages/spaces/sub-types/sub-types-list-page.component').then(
            (m) => m.SubTypesListPageComponent,
          ),
        title: 'Space sub-types — Admin',
      },
      // Capabilities catalogue (Space Health Scoring) — list + per-capability
      // coverage editor. Top-level Workplace rail item.
      {
        path: 'workplace/capabilities',
        loadComponent: () =>
          import('../features/settings/pages/capabilities/capabilities-list-page.component').then(
            (m) => m.CapabilitiesListPageComponent,
          ),
        title: 'Capabilities — Admin',
      },
      {
        path: 'workplace/capabilities/:key',
        loadComponent: () =>
          import('../features/settings/pages/capabilities/capability-detail-page.component').then(
            (m) => m.CapabilityDetailPageComponent,
          ),
        title: 'Capability — Admin',
      },
      {
        path: 'workplace/spaces/:id',
        loadComponent: () =>
          import('../features/settings/pages/spaces/space-detail-page.component').then(
            (m) => m.SpaceDetailPageComponent,
          ),
        canDeactivate: [spaceDetailCanDeactivateGuard],
        title: 'Space — Admin',
      },
      // Screens — the NEW Screens admin area (list + per-screen detail). The
      // `screens/templates` literal route lands with B11; `screens/:id` is the
      // detail page and carries the (component-agnostic) dirty can-deactivate
      // guard so unsaved name/template/auth edits prompt before navigation.
      // No route feature-flag guard: admins can always reach the Screens
      // management surface (inherits the parent [authGuard, adminGuard]). The
      // `screens` catalogue capability still governs the end-user/runtime
      // feature — it just no longer blocks the admin UI.
      {
        path: 'workplace/screens',
        loadComponent: () =>
          import('./screens/admin-screens-page.component').then((m) => m.AdminScreensPageComponent),
        title: 'Screens — Admin',
      },
      // `screens/templates` MUST precede `screens/:id` so the literal segment
      // wins over the param route (otherwise `:id` swallows "templates").
      {
        path: 'workplace/screens/templates',
        loadComponent: () =>
          import('./screens/screen-templates-page.component').then(
            (m) => m.ScreenTemplatesPageComponent,
          ),
        title: 'Screen templates — Admin',
      },
      {
        path: 'workplace/screens/:id',
        loadComponent: () =>
          import('./screens/screen-detail-page.component').then((m) => m.ScreenDetailPageComponent),
        canDeactivate: [spaceDetailCanDeactivateGuard],
        title: 'Screen — Admin',
      },
      // Signage — the ScreenCloud-style CMS (fleet + playlists). Distinct from
      // the `screens` pairing admin above. Literal child routes precede params.
      {
        path: 'workplace/signage',
        pathMatch: 'full',
        redirectTo: 'workplace/signage/screens',
      },
      {
        path: 'workplace/signage/screens',
        loadComponent: () =>
          import('./signage/screens/signage-screens-list-page.component').then(
            (m) => m.SignageScreensListPageComponent,
          ),
        title: 'Signage — Admin',
      },
      // `signage/screens/new` MUST precede `signage/screens/:id` so the
      // literal segment wins over the param route. This is the screen-creation
      // flow rooted in the admin shell (the old public /signage/setup
      // redirects here).
      {
        path: 'workplace/signage/screens/new',
        loadComponent: () =>
          import('./signage/screens/signage-screen-wizard.component').then(
            (m) => m.SignageScreenWizardComponent,
          ),
        title: 'New signage screen — Admin',
      },
      {
        path: 'workplace/signage/playlists',
        loadComponent: () =>
          import('./signage/playlists/signage-playlists-list-page.component').then(
            (m) => m.SignagePlaylistsListPageComponent,
          ),
        title: 'Signage playlists — Admin',
      },
      {
        path: 'workplace/signage/screencloud',
        loadComponent: () =>
          import('./signage/screencloud/signage-screencloud-content-page.component').then(
            (m) => m.SignageScreenCloudContentPageComponent,
          ),
        title: 'ScreenCloud content — Admin',
      },
      {
        path: 'workplace/signage/playlists/:id',
        loadComponent: () =>
          import('./signage/playlists/signage-playlist-editor-page.component').then(
            (m) => m.SignagePlaylistEditorPageComponent,
          ),
        title: 'Playlist — Admin',
      },
      {
        path: 'workplace/signage/screens/:id',
        loadComponent: () =>
          import('./signage/screens/signage-screen-detail-page.component').then(
            (m) => m.SignageScreenDetailPageComponent,
          ),
        title: 'Screen — Admin',
      },
      // Roles — the access redesign IS the canonical list surface (three lenses
      // over one assignment dataset), and the existing role detail re-skinned
      // into the hero+tabs chrome (Identity / Actions / Assigned people).
      // /settings/access/roles* redirects here. The :id detail keeps its real
      // clone/validate/save/system-read-only lifecycle, so it carries a
      // dirty-state can-deactivate guard.
      {
        // Roles allocation & visibility — one surface, three lenses (role /
        // division / person) over one assignment dataset, plus the grant flow
        // with opt-in division propagation. Lens + selection live in the query
        // string (`?lens=division&division=…`) so a view is shareable.
        //
        // This IS the "Roles" rail destination: the roles access redesign is
        // now the canonical Roles surface (the old catalogue list was retired).
        // Previously gated on `access.scoped-roles` at `people/access`; the gate
        // was dropped so every tenant lands on the redesign. Mutating controls
        // still require `role:manage`, and the server is the authority on that.
        path: 'people/roles',
        loadComponent: () =>
          import('./access/access-page.component').then((m) => m.AccessPageComponent),
        title: 'Roles — Admin',
      },
      {
        // ESG / Scope 3 employee-commuting report aggregated from the
        // profile "how do you usually travel to work?" answers.
        path: 'governance/commuting-esg',
        loadComponent: () =>
          import('./commuting-esg/admin-commuting-esg-page.component').then(
            (m) => m.AdminCommutingEsgPageComponent,
          ),
        title: 'Commuting & ESG — Admin',
      },
      {
        path: 'governance/gamification-effectiveness',
        canActivate: [gamificationAnalyticsGuard()],
        loadComponent: () =>
          import('./gamification-effectiveness/admin-gamification-effectiveness-page.component').then(
            (m) => m.AdminGamificationEffectivenessPageComponent,
          ),
        title: 'Gamification effectiveness — Admin',
      },
      {
        path: 'people/roles/:id',
        loadComponent: () =>
          import('../features/settings/pages/access/roles/role-detail-page.component').then(
            (m) => m.RoleDetailPageComponent,
          ),
        canDeactivate: [roleDetailCanDeactivateGuard],
        title: 'Role — Admin',
      },
      // Locations — the existing card-grid list + rich 12-section detail,
      // re-skinned into the admin chrome (paper-page-head/kpi list +
      // paper-page-head hero + paper-admin-tabs detail) and rooted in;
      // /settings/locations* redirects here. The detail keeps its dirty +
      // 8-mutation save, so it carries the (component-agnostic) dirty guard.
      {
        path: 'estate/locations',
        loadComponent: () =>
          import('../features/estate/locations/locations-page.component').then(
            (m) => m.LocationsPageComponent,
          ),
        title: 'Locations — Admin',
      },
      {
        path: 'estate/policy-migration/run',
        canActivate: [policyManagementGuard()],
        loadComponent: () => import('../features/estate/locations/policy-migration-run-page.component').then(m => m.PolicyMigrationRunPageComponent),
        title: 'Move policies — Admin',
      },
      {
        path: 'estate/policy-migration',
        canActivate: [policyManagementGuard()],
        loadComponent: () =>
          import('../features/estate/locations/policy-migration-estate-page.component').then(
            (m) => m.PolicyMigrationEstatePageComponent,
          ),
        title: 'Check policies — Admin',
      },
      // The read-only legacy → Policy V2 comparison for ONE building, opened
      // by the adoption panel's "Review policies". Declared BEFORE the bare
      // `:id` detail so the deeper URL cannot be shadowed, and guarded by the
      // Policies guard rather than a rail entry: it is a per-record surface
      // (same as governance/policies/location/:id), it shows the customer's
      // policy configuration, and nothing behind it mutates anything.
      {
        path: 'estate/locations/:id/policy-migration',
        canActivate: [policyManagementGuard()],
        loadComponent: () =>
          import('../features/estate/locations/location-policy-migration-page.component').then(
            (m) => m.LocationPolicyMigrationPageComponent,
          ),
        title: 'Policy review — Admin',
      },
      // Door kinds — the vocabulary every building's Doors section picks from
      // (a visitor reason names a KIND of door, never a door). Tenant-wide, so
      // its own page: a rail child of Locations, not a location section.
      {
        path: 'estate/door-kinds',
        loadComponent: () =>
          import('../features/estate/doors/door-kinds-page.component').then(
            (m) => m.DoorKindsPageComponent,
          ),
        title: 'Door kinds — Admin',
      },
      // ── Visitors (capability-gated) ───────────────────────────
      // Reasons, the categories they report under, the questionnaires they
      // ask (shared across sites + per-site additions) and the category
      // report. Each route carries the action its page cannot work without;
      // the API enforces the finer rules (estate-wide shared saves, locked
      // questions, per-site opt-out) on every write.
      // Visitor types (stored as reasons — the path keeps the API's word). The
      // list, a full create page, and each one's own page (2a, `?tab=`); the
      // side drawer they replace is gone. `new` is declared before `:id`.
      {
        // A questionnaire-only admin reaches a visitor type's Questionnaire tab
        // through this list; creating and retiring stay behind reason:manage.
        path: 'visitors/reasons',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REASON_MANAGE, Action.VISITOR_QUESTIONNAIRE_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/visitors/visitor-reasons-page.component').then(
            (m) => m.VisitorReasonsPageComponent,
          ),
        title: 'Visitor types — Admin',
      },
      {
        path: 'visitors/reasons/new',
        canActivate: [featureFlagGuard('visitors'), anyActionGuard([Action.VISITOR_REASON_MANAGE])],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/visitors/visitor-reason-create-page.component').then(
            (m) => m.VisitorReasonCreatePageComponent,
          ),
        title: 'New visitor type — Admin',
      },
      {
        // The builder is this page's Questionnaire tab: questionnaire:manage
        // alone opens it, on that tab and Visualiser only.
        path: 'visitors/reasons/:id',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REASON_MANAGE, Action.VISITOR_QUESTIONNAIRE_MANAGE]),
        ],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/visitors/visitor-reason-detail-page.component').then(
            (m) => m.VisitorReasonDetailPageComponent,
          ),
        title: 'Visitor type — Admin',
      },
      // Badge templates — what a visitor is handed at sign-in, one per reason.
      // Every badge-template operation takes visitor:reason:manage on the API,
      // and so does pointing a reason at one (updateReason).
      {
        path: 'visitors/badges',
        canActivate: [featureFlagGuard('visitors'), anyActionGuard([Action.VISITOR_REASON_MANAGE])],
        loadComponent: () =>
          import('../features/visitors/visitor-badge-templates-page.component').then(
            (m) => m.VisitorBadgeTemplatesPageComponent,
          ),
        title: 'Badge templates — Admin',
      },
      {
        // `new` is the create form (`?reason=` preselects the reason).
        path: 'visitors/badges/:id',
        canActivate: [featureFlagGuard('visitors'), anyActionGuard([Action.VISITOR_REASON_MANAGE])],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/visitors/visitor-badge-template-detail-page.component').then(
            (m) => m.VisitorBadgeTemplateDetailPageComponent,
          ),
        title: 'Badge template — Admin',
      },
      {
        // Readable by anyone who can see a visit (the reason editor and the
        // report name these); writes need visitor:reason:manage.
        path: 'visitors/categories',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISIT_VIEW, Action.VISITOR_REASON_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/visitors/visitor-categories-page.component').then(
            (m) => m.VisitorCategoriesPageComponent,
          ),
        title: 'Visitor categories — Admin',
      },
      {
        // What a first-time visitor fills in for one reason at one site
        // (`?reason=&site=`), clicked through one question at a time. Read-only
        // — nothing is saved — so no dirty guard. Same action as the builder
        // (a visitor type's Questionnaire tab) it is opened from; declared
        // before the old builder path so the deeper URL is never shadowed.
        path: 'visitors/questionnaires/preview',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_QUESTIONNAIRE_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/visitors/visitor-questionnaire-previewer-page.component').then(
            (m) => m.VisitorQuestionnairePreviewerPageComponent,
          ),
        title: 'Questionnaire preview — Admin',
      },
      {
        // The questionnaire belongs to its visitor type (Brad, 25 Sep 2026):
        // the builder is that type's Questionnaire tab. Old links — the rail
        // entry, bookmarks, `?reason=&type=` from before — land there.
        path: 'visitors/questionnaires',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) => questionnaireTabRedirect('/visitors', queryParams),
      },
      {
        // The organisation's version of every email a visitor or their host is
        // sent. Each visitor type words the three visitor-facing ones for
        // itself on its own Emails tab. The catalogue read (`ownEmailTemplates`)
        // takes email-template:read:own, or visitor:reason:manage limited to
        // the visitor:* rows — the same two the rail entry lists.
        path: 'visitors/emails',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.EMAIL_TEMPLATE_READ_OWN, Action.VISITOR_REASON_MANAGE]),
          visitorEmailLinkGuard,
        ],
        loadComponent: () =>
          import('./email-templates/admin-email-templates-page.component').then(
            (m) => m.AdminEmailTemplatesPageComponent,
          ),
        data: { emailScope: 'visitors' },
        title: 'Visitor emails — Admin',
      },
      // Requirements (what a visitor must meet before arrival) and the
      // documents they read and sign — both behind visitor:requirement:manage,
      // which every requirement and document operation takes on the API.
      {
        path: 'visitors/requirements',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REQUIREMENT_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/visitors/visitor-requirements-page.component').then(
            (m) => m.VisitorRequirementsPageComponent,
          ),
        title: 'Visitor requirements — Admin',
      },
      {
        path: 'visitors/requirements/new',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REQUIREMENT_MANAGE]),
        ],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/visitors/visitor-requirement-create-page.component').then(
            (m) => m.VisitorRequirementCreatePageComponent,
          ),
        title: 'New requirement — Admin',
      },
      {
        path: 'visitors/requirements/:id',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REQUIREMENT_MANAGE]),
        ],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/visitors/visitor-requirement-detail-page.component').then(
            (m) => m.VisitorRequirementDetailPageComponent,
          ),
        title: 'Requirement — Admin',
      },
      {
        path: 'visitors/documents',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REQUIREMENT_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/visitors/visitor-documents-page.component').then(
            (m) => m.VisitorDocumentsPageComponent,
          ),
        title: 'Visitor documents — Admin',
      },
      {
        path: 'visitors/documents/:id',
        canActivate: [
          featureFlagGuard('visitors'),
          anyActionGuard([Action.VISITOR_REQUIREMENT_MANAGE]),
        ],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/visitors/visitor-document-detail-page.component').then(
            (m) => m.VisitorDocumentDetailPageComponent,
          ),
        title: 'Visitor document — Admin',
      },
      {
        path: 'visitors/report',
        canActivate: [featureFlagGuard('visitors'), anyActionGuard([Action.VISIT_VIEW])],
        loadComponent: () =>
          import('../features/visitors/visitor-category-report-page.component').then(
            (m) => m.VisitorCategoryReportPageComponent,
          ),
        title: 'Visits by category — Admin',
      },
      {
        path: 'estate/locations/:id',
        loadComponent: () =>
          import('../features/estate/locations/location-detail-page.component').then(
            (m) => m.LocationDetailPageComponent,
          ),
        canDeactivate: [spaceDetailCanDeactivateGuard],
        title: 'Location — Admin',
      },
      // People — fresh-look directory (new admin-people-page on the real
      // employeeDirectory + peopleStats backend) reusing the existing
      // role/profile slide-over panels; no :id route (per-person is a drawer).
      // /settings/access/people redirects here.
      {
        path: 'people/directory',
        loadComponent: () =>
          import('./people/admin-people-page.component').then((m) => m.AdminPeoplePageComponent),
        title: 'People — Admin',
      },
      // Organisation — the fresh-look platform-settings surface, rooted into
      // the admin shell (?tab=-driven: messaging / authentication / calendar /
      // branding / flags / approvals). Single page composing six section
      // components; /settings/organisation* redirects here. Inherits the
      // parent [authGuard, adminGuard]; per-concern services gate server-side.
      {
        path: 'platform/organisation',
        loadComponent: () =>
          import('./organisation/admin-organisation-page.component').then(
            (m) => m.AdminOrganisationPageComponent,
          ),
        title: 'Organisation — Admin',
      },
      // Customer access and Support staff moved into the operator console
      // (`/operator/access`, `/operator/staff`). Redirects keep old links and
      // bookmarks working, query string included (`?lens=`, `?email=`).
      { path: 'platform/customer-access', redirectTo: '/operator/access' },
      { path: 'platform/support-staff', redirectTo: '/operator/staff' },
      // Policies (One Paper, COMMON rulings 18 and 22): ONE page, a place at a
      // time, every setting of the v3 catalogue in tabs, Save as draft or
      // Apply, no approval step. This is the only Policies system: where a
      // building has not moved to Policies v3 yet, the page says so and never
      // errors, and there is no older screen to send anyone to.
      {
        path: 'governance/policies',
        canActivate: [policyManagementGuard()],
        loadChildren: () =>
          import('../features/settings/pages/policies/list/policies-list.routes').then((m) => m.POLICIES_LIST_ROUTES),
        title: 'Policies — Admin',
        pathMatch: 'full',
      },
      // Every older policy URL (explorer, library, by-location, location/:id,
      // Proposals ...) lands on the list. See policy-legacy-redirects.ts.
      ...LEGACY_POLICY_REDIRECTS,
      // ── Divisions ─────────────────────────────────────────────
      // The existing list + detail components mounted into the admin
      // shell (no re-skin). `/settings/divisions*` redirects here.
      // `divisions/:id` declared AFTER the literal list route.
      {
        path: 'platform/divisions',
        loadComponent: () =>
          import('../features/settings/pages/divisions/divisions-page.component').then(
            (m) => m.DivisionsPageComponent,
          ),
        title: 'Divisions — Admin',
      },
      {
        path: 'platform/apps',
        loadComponent: () =>
          // Canonical NEW fork (features/platform/apps) — the admin/apps fork it
          // superseded (preview-session backend + custom-logo picker grafted in)
          // is now orphaned. platform.routes.ts is a dead parallel surface, so
          // this admin.routes.ts entry stays and is repointed, not deleted.
          import('../features/platform/apps/admin-apps-page.component').then(
            (m) => m.AdminAppsPageComponent,
          ),
        title: 'Apps — Admin',
      },
      {
        path: 'platform/divisions/:id',
        loadComponent: () =>
          import('../features/settings/pages/division-detail/division-detail-page.component').then(
            (m) => m.DivisionDetailPageComponent,
          ),
        title: 'Division — Admin',
      },
      // ── Onboarding ────────────────────────────────────────────
      // `onboarding/greenfield` is a literal sibling, declared AFTER
      // the bare `onboarding` route (no conflicting param).
      // The onboarding workbench is SUPERSEDED by Import and export, which is
      // now the one way to bring an estate online. It is gone from the rail,
      // the section catalogue and the AI helper; this redirect closes the last
      // door — an old bookmark lands on the successor instead of a page nobody
      // maintains. `pathMatch: 'full'` keeps it from swallowing the retired
      // greenfield path below, which redirects somewhere else.
      {
        path: 'estate/onboarding',
        pathMatch: 'full',
        redirectTo: 'estate/import',
      },
      // The greenfield wizard is RETIRED: creating a customer is the operator
      // console's Create customer page, which makes the customer without
      // switching into it first. An old bookmark lands there.
      {
        path: 'estate/onboarding/greenfield',
        pathMatch: 'full',
        redirectTo: '/operator/customers/new',
      },
      {
        path: 'estate/import',
        loadComponent: () =>
          import('./import/admin-import-wizard-page.component').then(
            (m) => m.AdminImportWizardPageComponent,
          ),
        title: 'Import and export — Admin',
      },
      // ── Integrations ──────────────────────────────────────────
      // The 3 literal sub-paths (activity / asset-discovery /
      // microsoft/live) MUST precede the `:provider` catch-all so they
      // aren't swallowed. Admin-only, unchanged: the legacy /settings parent
      // already carried adminGuard and Angular accumulates guards down the
      // tree, so the child's [authGuard] never relaxed access. No regression.
      {
        path: 'platform/integrations',
        loadComponent: () =>
          import('../features/settings/pages/integrations/integrations-page.component').then(
            (m) => m.IntegrationsPageComponent,
          ),
        title: 'Integrations — Admin',
      },
      {
        path: 'platform/integrations/activity',
        loadComponent: () =>
          import('../features/settings/pages/integrations/activity-page.component').then(
            (m) => m.IntegrationActivityPageComponent,
          ),
        title: 'Integrations · Activity — Admin',
      },
      {
        path: 'platform/integrations/asset-discovery',
        loadComponent: () =>
          import('../features/settings/pages/integrations/asset-discovery/integrations-page.component').then(
            (m) => m.AssetDiscoveryIntegrationsPageComponent,
          ),
        title: 'Integrations · Asset Discovery — Admin',
      },
      {
        path: 'platform/integrations/mass-mapping',
        loadComponent: () =>
          import('../features/settings/pages/integrations/mass-mapping/mass-mapping-page.component').then(
            (m) => m.MassMappingPageComponent,
          ),
        title: 'Integrations · AV mass mapping — Admin',
      },
      {
        path: 'platform/integrations/microsoft/live',
        loadComponent: () =>
          import('../features/settings/pages/integrations/microsoft-live-page.component').then(
            (m) => m.MicrosoftLivePageComponent,
          ),
        title: 'Microsoft 365 · Live — Admin',
      },
      {
        path: 'platform/integrations/:provider',
        loadComponent: () =>
          import('../features/settings/pages/integrations/integration-detail-page.component').then(
            (m) => m.IntegrationDetailPageComponent,
          ),
        title: 'Integration — Admin',
      },
      // ── Building alerts + Closures ────────────────────────────
      // Two surfaces over the same BuildingAlerts collection: `alerts`
      // lists NOTICES (informational, never block bookings) on the
      // standard admin template, and `closures` is the purpose-built
      // closures workspace (schedule board + lifecycle groups + detail
      // rail + guided editor) for CLOSURES (blockBookings=true — spaces
      // out of service for a window). No route feature-flag guard:
      // admins can always reach + manage both (inherits the parent
      // [authGuard, adminGuard]). The `alerts.building` catalogue
      // capability still exists for entitlement, but no longer blocks the
      // admin UI.
      {
        path: 'communications/alerts',
        canActivate: [buildingAlertManagementGuard()],
        loadComponent: () =>
          import('../features/settings/pages/alerts/alerts-page.component').then(
            (m) => m.AlertsPageComponent,
          ),
        title: 'Building alerts — Admin',
      },
      {
        path: 'workplace/closures',
        canActivate: [buildingAlertManagementGuard()],
        loadComponent: () =>
          import('../features/settings/pages/closures/closures-page.component').then(
            (m) => m.ClosuresPageComponent,
          ),
        title: 'Closures — Admin',
      },
      // ── Content management (catalogue-flag gated) ─────────────
      {
        path: 'workplace/content',
        canActivate: [featureFlagGuard('content.viewer')],
        loadComponent: () =>
          import('../features/settings/pages/content/content-management-page.component').then(
            (m) => m.ContentManagementPageComponent,
          ),
        title: 'Content — Admin',
      },
      {
        // WP32 live events — READ-ONLY list of what is scheduled and what is
        // on. Gated on `broadcast.playlist` rather than a key of its own:
        // there is no live-events flag in the backend catalogue, and a live
        // event only reaches an audience by becoming a broadcast slot, so a
        // customer without the playlist cannot have one anyone can see.
        // Layers 2 and 3 of the gating live in the component.
        path: 'workplace/live-events',
        canActivate: [featureFlagGuard('broadcast.playlist')],
        loadComponent: () =>
          import('../features/settings/pages/live-events/live-events-page.component').then(
            (m) => m.LiveEventsPageComponent,
          ),
        title: 'Upcoming live events — Admin',
      },
      // ── Campuses ──────────────────────────────────────────────
      // `campuses/:id` keeps its dirty-state can-deactivate guard;
      // declared AFTER the literal list route.
      {
        path: 'estate/campuses',
        loadComponent: () =>
          // Canonical NEW fork (features/estate/campuses) — the fuller,
          // map-engine-backed campuses. The slim features/settings/pages/campuses
          // fork it supersedes is now orphaned.
          import('../features/estate/campuses/campuses-page.component').then(
            (m) => m.CampusesPageComponent,
          ),
        title: 'Campuses — Admin',
      },
      {
        path: 'estate/campuses/:id',
        // Route-scoped map engine — keeps Ricoh Maps /core + turf out of the
        // initial bundle; the estate detail's map tab breaks without it.
        providers: [provideCampusMapEngine()],
        loadComponent: () =>
          import('../features/estate/campuses/campus-detail-page.component').then(
            (m) => m.CampusDetailPageComponent,
          ),
        canDeactivate: [spaceDetailCanDeactivateGuard],
        title: 'Campus — Admin',
      },
      // ── Assets (catalogue-flag gated: `assets.registry`) ──────
      // Literal `assets/new` MUST precede the `assets/:id` param route.
      {
        path: 'workplace/assets',
        canActivate: [featureFlagGuard('assets.registry')],
        loadComponent: () =>
          import('../features/settings/pages/assets/asset-manager-page.component').then(
            (m) => m.AssetManagerPageComponent,
          ),
        title: 'Assets — Admin',
      },
      {
        path: 'workplace/assets/new',
        canActivate: [featureFlagGuard('assets.registry')],
        loadComponent: () =>
          import('../features/settings/pages/assets/asset-create-page.component').then(
            (m) => m.AssetCreatePageComponent,
          ),
        title: 'New asset — Admin',
      },
      {
        path: 'workplace/assets/:id',
        canActivate: [featureFlagGuard('assets.registry')],
        loadComponent: () =>
          import('../features/settings/pages/assets/asset-detail-page.component').then(
            (m) => m.AssetDetailPageComponent,
          ),
        title: 'Asset — Admin',
      },
      // ── Problem categories (MCW-542) ──────────────────────────
      // Sibling to Assets. Library of platform-default + customer-custom
      // categories that group asset types and curate their common problems.
      // (Renamed from "asset categories" per Director-of-Innovation review.)
      // Gated with Common problems on the `issues.categories` capability.
      {
        path: 'workplace/problem-categories',
        canActivate: [featureFlagGuard('issues.categories')],
        loadComponent: () =>
          import('../features/settings/pages/problem-categories/problem-categories-list-page.component').then(
            (m) => m.ProblemCategoriesListPageComponent,
          ),
        title: 'Problem categories — Admin',
      },
      // ── Common problems (MCW-543) ─────────────────────────────
      {
        path: 'workplace/common-problems',
        canActivate: [featureFlagGuard('issues.categories')],
        loadComponent: () =>
          import('../features/settings/pages/common-problems/common-problems-list-page.component').then(
            (m) => m.CommonProblemsListPageComponent,
          ),
        title: 'Common problems — Admin',
      },
      // ── Access & audit ────────────────────────────────────────
      // `permissions` (the admin rail item) mounts the effective-
      // permissions check page; `features` mounts the catalogue feature
      // list. (/settings/access/permissions + /settings/access/features
      // redirect here.)
      {
        path: 'people/permissions',
        loadComponent: () =>
          import('../features/settings/pages/permissions-check/permissions-check-page.component').then(
            (m) => m.PermissionsCheckPageComponent,
          ),
        title: 'Effective permissions — Admin',
      },
      {
        // The capability catalogue, formerly Organisation's "Capabilities" tab
        // — an area in its own right, so the rail names it directly.
        path: 'platform/capabilities',
        loadComponent: () =>
          import('./organisation/admin-capabilities-page.component').then(m => m.AdminCapabilitiesPageComponent),
        title: 'Modules — Admin',
        canDeactivate: [(component: { canLeave(): boolean | Promise<boolean> }) => component.canLeave()],
      },
      // HOLD (rc/settings-2026-10-18): the Security and privacy page waits for
      // Nath's sign-off of its copy (local-stack/lanes/settings-app-1016/SECURITY-COPY.md).
      // Revert this commit to release it: route, rail entry and Modules links.
      {
        // The Game Centre: which games each division sees, and the rules. Sits
        // beside Modules (which carries the games.* capability switches) and
        // takes the same division scope. The preview is /preview/game-centre.
        path: 'platform/games',
        canActivate: [anyActionGuard([Action.GAME_CONFIG_MANAGE])],
        providers: [{ provide: GAMES_SETTINGS_API, useClass: LiveGamesSettingsApi }],
        loadComponent: () =>
          import('../features/settings/pages/games/games-settings-page.component').then(
            (m) => m.GamesSettingsPageComponent,
          ),
        title: 'Game Centre — Admin',
      },
      {
        // Read-only companion: Capabilities turns modules on, this says whether
        // the DATA behind them is actually there.
        path: 'platform/insights-readiness',
        loadComponent: () =>
          import('./organisation/admin-insights-readiness-page.component').then(m => m.AdminInsightsReadinessPageComponent),
        title: 'Insights readiness — Admin',
      },
      {
        // Retired 11 Sep 2026 — this was a second copy of the Capabilities
        // surface over the same service and the same catalogue. The redirect is
        // kept rather than the route deleted: the page had a rail entry for
        // months, so bookmarks and pasted links exist, and a 404 on one would
        // read as the feature being gone rather than moved.
        path: 'platform/features',
        redirectTo: 'platform/capabilities',
        pathMatch: 'full',
      },
      // ── Activity console ──────────────────────────────────────
      {
        path: 'platform/activity',
        loadComponent: () =>
          import('../features/settings/pages/activity/activity-page.component').then(
            (m) => m.ActivityPageComponent,
          ),
        title: 'Activity — Admin',
      },
      // ── Catering (catalogue-flag gated subtree) ───────────────
      {
        path: 'workplace/catering',
        canActivate: [featureFlagGuard('catering'), anyActionGuard([Action.CATERING_MANAGE])],
        children: [
          { path: '', redirectTo: 'vendors', pathMatch: 'full' },
          {
            // Moved to the Servicing rail as its own destination (see
            // `servicing/catering-providers`). The redirect is kept rather than
            // the route deleted: this path had a rail entry for months, so
            // bookmarks and pasted links exist.
            path: 'vendors',
            redirectTo: '/servicing/catering-providers',
            pathMatch: 'full',
          },
          {
            // A catering vendor's id === its Service-provider id (decision 2a) —
            // redirect the legacy vendor-detail deep link onto the SP detail page.
            path: 'vendors/:vendorId',
            redirectTo: ({ params }) =>
              inject(Router).createUrlTree([
                '/servicing/catering-providers',
                params['vendorId'],
              ]),
          },
          {
            // The menu editor is now the conditional "Menu" tab on SP detail —
            // redirect onto it with the tab pre-selected (SP detail honours ?tab).
            path: 'menus/:vendorId',
            redirectTo: ({ params }) =>
              inject(Router).createUrlTree(
                ['/servicing/catering-providers', params['vendorId']],
                { queryParams: { tab: 'catering' } },
              ),
          },
          {
            path: 'orders',
            loadComponent: () =>
              import('../features/settings/pages/catering-orders/catering-orders-page.component').then(
                (m) => m.CateringOrdersPageComponent,
              ),
            title: 'Catering · Orders — Admin',
          },
        ],
      },
      // ── Requestable Services (Fulfillment) ────────────────────
      // `services/new` (literal) must precede `services/:id`.
      {
        path: 'workplace/services',
        data: { accessStateArea: 'services' },
        canActivate: [featureFlagGuard('fulfillment')],
        loadComponent: () =>
          import('../features/workplace/services/services-list-page.component').then(
            (m) => m.ServicesListPageComponent,
          ),
        title: 'Services — Admin',
      },
      {
        path: 'workplace/services/new',
        canActivate: [featureFlagGuard('fulfillment')],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/workplace/services/service-editor-page.component').then(
            (m) => m.ServiceEditorPageComponent,
          ),
        title: 'New service — Admin',
      },
      {
        path: 'workplace/services/:id',
        canActivate: [featureFlagGuard('fulfillment')],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/workplace/services/service-editor-page.component').then(
            (m) => m.ServiceEditorPageComponent,
          ),
        title: 'Service — Admin',
      },
      // ── Service Providers (Servicing) + Coverage ──────────────
      // Gated on the base `service-providers` flag; the Pro surfaces
      // (composer preview, routing test, destinations) are gated inside
      // the components via *imFeatureFlag.
      //
      // TWO DISJOINT DIRECTORIES, one component. Service providers are ROUTED
      // TO (coverage picks one owner per asset / space × service); catering
      // providers are ORDERED FROM (a service area of buildings, many caterers
      // per building, the employee chooses). They are configured through
      // different tabs and different models, so the rail gives each its own
      // destination and neither list shows the other's rows.
      {
        path: 'servicing/service-providers',
        data: { accessStateArea: 'service-providers', excludeTypes: ['catering'] },
        canActivate: [
          featureFlagGuard('service-providers'),
          anyActionGuard([Action.SERVICE_PROVIDER_VIEW, Action.SERVICE_PROVIDER_MANAGE]),
        ],
        loadComponent: () =>
          // Canonical servicing fork (features/servicing/providers) — a superset
          // of the removed admin/service-providers list (i18n, presetType
          // sub-directory, view preference, exception-first sort).
          import('../features/servicing/providers/service-providers-list-page.component').then(
            (m) => m.ServiceProvidersListPageComponent,
          ),
        title: 'Service providers — Admin',
      },
      {
        // The catering half of the same directory. Gated on `catering:manage`
        // rather than the service-provider actions, matching the Menu tab and
        // the catering mutations it leads to.
        path: 'servicing/catering-providers',
        data: { accessStateArea: 'service-providers', presetType: 'catering' },
        canActivate: [
          featureFlagGuard('catering'),
          anyActionGuard([Action.CATERING_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/servicing/providers/service-providers-list-page.component').then(
            (m) => m.ServiceProvidersListPageComponent,
          ),
        title: 'Catering providers — Admin',
      },
      {
        /**
         * A caterer's detail is the SAME COMPONENT as a service provider's —
         * they differ by tab set, not by page — but it is mounted at its own
         * path rather than redirected onto the other one.
         *
         * The rail is why. `matchActiveNav` resolves the active item by
         * longest-route-prefix, so a caterer served from
         * `/servicing/service-providers/:id` lit up "Service providers" — the
         * rail said one directory while the breadcrumb said the other. Giving
         * the surface its own URL makes the path itself the truth, for the
         * rail, the breadcrumb, a bookmark and the back button alike.
         */
        path: 'servicing/catering-providers/:id',
        data: { accessStateArea: 'service-providers', entryPreset: 'catering' },
        canActivate: [
          featureFlagGuard('catering'),
          anyActionGuard([Action.CATERING_MANAGE]),
        ],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          import('../features/servicing/providers/service-provider-detail-page.component').then(
            (m) => m.ServiceProviderDetailPageComponent,
          ),
        title: 'Catering provider — Admin',
      },
      {
        path: 'servicing/service-providers/:id',
        canActivate: [
          featureFlagGuard('service-providers'),
          anyActionGuard([Action.SERVICE_PROVIDER_VIEW, Action.SERVICE_PROVIDER_MANAGE]),
        ],
        canDeactivate: [spaceDetailCanDeactivateGuard],
        loadComponent: () =>
          // Detail consolidated onto the canonical servicing fork.
          import('../features/servicing/providers/service-provider-detail-page.component').then(
            (m) => m.ServiceProviderDetailPageComponent,
          ),
        title: 'Service provider — Admin',
      },
      // `coverage/new` (literal) declared before `coverage/:id/edit` (param).
      {
        path: 'servicing/coverage',
        data: { accessStateArea: 'coverage' },
        canActivate: [
          featureFlagGuard('service-providers'),
          anyActionGuard([Action.SERVICE_PROVIDER_VIEW, Action.SERVICE_PROVIDER_MANAGE]),
        ],
        loadComponent: () =>
          // Canonical servicing fork (features/servicing/coverage). The composer
          // routes below (`coverage/new`, `coverage/:id/edit`) are consolidated
          // onto the same fork.
          import('../features/servicing/coverage/coverage-list-page.component').then(
            (m) => m.CoverageListPageComponent,
          ),
        title: 'Coverage — Admin',
      },
      {
        path: 'servicing/coverage/new',
        canActivate: [
          featureFlagGuard('service-providers'),
          anyActionGuard([Action.SERVICE_PROVIDER_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/servicing/coverage/coverage-composer-page.component').then(
            (m) => m.CoverageComposerPageComponent,
          ),
        title: 'New coverage — Admin',
      },
      {
        path: 'servicing/coverage/:id/edit',
        canActivate: [
          featureFlagGuard('service-providers'),
          anyActionGuard([Action.SERVICE_PROVIDER_MANAGE]),
        ],
        loadComponent: () =>
          import('../features/servicing/coverage/coverage-composer-page.component').then(
            (m) => m.CoverageComposerPageComponent,
          ),
        title: 'Edit coverage — Admin',
      },
      // ── Dev / tooling surfaces ────────────────────────────────
      // Gated by devOnlyGuard (PRO-426): reachable in dev/local builds, but a
      // direct URL hit in production falls through to Overview so these
      // engineering tools never surface to customers.
      {
        path: 'platform/brand-test',
        canActivate: [devOnlyGuard],
        loadComponent: () =>
          import('../features/settings/pages/brand-test/brand-test-page.component').then(
            (m) => m.BrandTestPageComponent,
          ),
        title: 'Brand Test — Admin',
      },
      // Internal ingest is unavailable, including local development entry points.
      { path: 'estate/map-ingest', pathMatch: 'full', redirectTo: '/estate/map' },
      { path: 'estate/map-ingest/upload', pathMatch: 'full', redirectTo: '/estate/map' },
      // `platform/component-gallery`, the old design-system gallery, is gone:
      // its URL leaves for the Ricoh Paper docs site, from a route declared
      // ahead of this layout (`core/guards/paper-docs-redirect.ts`).
      {
        path: 'estate/map',
        canDeactivate: [(component: { canLeave(): boolean | Promise<boolean> }) => component.canLeave()],
        loadComponent: () =>
          import('./map/admin-map-page.component').then((m) => m.AdminMapPageComponent),
        title: 'Indoor maps — Admin',
      },
      // `map/import` (literal) — the "Import furniture" wizard. Declared as its
      // own child so it resolves to the wizard page, not the map page. Same
      // leave guard as the map: the wizard asks first while an import runs.
      {
        path: 'estate/map/import',
        canDeactivate: [(component: { canLeave(): boolean | Promise<boolean> }) => component.canLeave()],
        loadComponent: () =>
          import('./map/import-wizard-page.component').then((m) => m.ImportWizardPageComponent),
        title: 'Indoor maps · Import furniture — Admin',
      },
      // `map/sync` (literal) — the staged floor-plan sync wizard (WP12):
      // scope → overrides → preview (awaiting confirmation) → result. The
      // ONLY admin entry point for staged Archilogic syncs; deep-linked from
      // Settings → Locations → Map configuration with ?location=<id>.
      // `map/setup` (literal) — Set up plans: pair a building's Archilogic
      // floors with its Spaces floors, create and link them, then hand over to
      // the sync wizard. ?location=<id>.
      {
        path: 'estate/map/setup',
        loadComponent: () =>
          import('./map/setup/building-setup-page.component').then((m) => m.BuildingSetupPageComponent),
        title: 'Indoor maps · Set up plans — Admin',
      },
      {
        path: 'estate/map/sync',
        loadComponent: () =>
          import('./map/sync-wizard-page.component').then((m) => m.SyncWizardPageComponent),
        title: 'Indoor maps · Sync from Archilogic — Admin',
      },
      { path: '**', redirectTo: 'overview' },
    ],
  },
];
